import { useCallback, useEffect, useMemo, useState } from 'react'
import apiClient, { setAuthToken } from '../api/apiClient'
import type { AxiosError } from 'axios'

type AuthUser = {
  id: string
  email: string
}

type Session = {
  userId: string
  email: string
  createdAt?: string
}

type SessionErrorPayload = {
  error?: string
}

const TOKEN_KEY = 'nz_tax_auth_token'
const AUTH_MODE = import.meta.env.VITE_AUTH_MODE || 'local'
const NO_AUTH_MODE = AUTH_MODE === 'none'
const LOCAL_AUTH_MODE = AUTH_MODE === 'local'
const LOCAL_DEMO_TOKEN = 'demo-local-token'
const DEMO_SESSION: Session = {
  userId: 'demo-user',
  email: 'demo@nztax.local',
}

function readStoredToken(): string | null {
  return window.localStorage.getItem(TOKEN_KEY)
}

function writeStoredToken(token: string | null) {
  if (token) {
    window.localStorage.setItem(TOKEN_KEY, token)
  } else {
    window.localStorage.removeItem(TOKEN_KEY)
  }
}

export const useAuth = () => {
  const [token, setTokenState] = useState<string | null>(() => {
    if (NO_AUTH_MODE) return 'demo-no-auth-token'
    const stored = readStoredToken()
    if (stored) return stored
    return LOCAL_AUTH_MODE ? LOCAL_DEMO_TOKEN : null
  })
  const [session, setSession] = useState<Session | null>(() => (NO_AUTH_MODE || LOCAL_AUTH_MODE ? DEMO_SESSION : null))
  const [loading, setLoading] = useState(!(NO_AUTH_MODE || LOCAL_AUTH_MODE))

  const applyToken = useCallback((nextToken: string | null) => {
    if (NO_AUTH_MODE) {
      setTokenState('demo-no-auth-token')
      setAuthToken(null)
      return
    }

    if (LOCAL_AUTH_MODE && !nextToken) {
      setTokenState(LOCAL_DEMO_TOKEN)
      setAuthToken(null)
      writeStoredToken(null)
      return
    }

    setTokenState(nextToken)
    writeStoredToken(nextToken)
    setAuthToken(nextToken)
  }, [])

  const refreshSession = useCallback(async () => {
    if (NO_AUTH_MODE) {
      setAuthToken(null)
      setSession(DEMO_SESSION)
      setLoading(false)
      return DEMO_SESSION
    }

    const currentToken = readStoredToken()
    if (!currentToken && !LOCAL_AUTH_MODE) {
      setSession(null)
      setLoading(false)
      return null
    }

    try {
      setAuthToken(currentToken || null)
      const response = await apiClient.get('/auth/session')
      const nextSession = response.data?.session || (LOCAL_AUTH_MODE ? DEMO_SESSION : null)
      if (!currentToken && LOCAL_AUTH_MODE) {
        setTokenState(LOCAL_DEMO_TOKEN)
      }
      setSession(nextSession)
      setLoading(false)
      return nextSession
    } catch (error: unknown) {
      const axiosError = error as AxiosError<SessionErrorPayload>
      const status = axiosError.response?.status
      const code = axiosError.response?.data?.error
      const isAuthFailure = status === 401 || code === 'INVALID_SESSION' || code === 'MISSING_TOKEN'
      if (!isAuthFailure) {
        // Keep current auth state on transient network failures to avoid forced logout.
        setLoading(false)
        return session
      }

      if (LOCAL_AUTH_MODE) {
        setAuthToken(null)
        setTokenState(LOCAL_DEMO_TOKEN)
        setSession(DEMO_SESSION)
        setLoading(false)
        return DEMO_SESSION
      }

      applyToken(null)
      setSession(null)
      setLoading(false)
      return null
    }
  }, [applyToken, session])

  useEffect(() => {
    void refreshSession()
  }, [refreshSession])

  const signup = useCallback(async (email: string, password: string): Promise<AuthUser> => {
    if (NO_AUTH_MODE) {
      return { id: DEMO_SESSION.userId, email: DEMO_SESSION.email }
    }
    const response = await apiClient.post('/auth/signup', { email, password })
    return response.data?.user
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    if (NO_AUTH_MODE) {
      setSession(DEMO_SESSION)
      setTokenState('demo-no-auth-token')
      return { id: DEMO_SESSION.userId, email: DEMO_SESSION.email }
    }
    const response = await apiClient.post('/auth/signin', { email, password })
    const nextToken = response.data?.token as string | undefined
    const user = response.data?.user as AuthUser | undefined
    if (!nextToken || !user) throw new Error('LOGIN_FAILED')
    applyToken(nextToken)
    await refreshSession()
    return user
  }, [applyToken, refreshSession])

  const logout = useCallback(async () => {
    if (NO_AUTH_MODE) {
      setSession(DEMO_SESSION)
      setTokenState('demo-no-auth-token')
      setAuthToken(null)
      return
    }
    try {
      if (readStoredToken()) {
        await apiClient.post('/auth/signout')
      }
    } catch {
      // ignore signout cleanup errors
    } finally {
      applyToken(null)
      setSession(null)
    }
  }, [applyToken])

  const isAuthenticated = Boolean(session && (token || LOCAL_AUTH_MODE))

  return useMemo(() => ({
    isAuthenticated,
    isLoading: loading,
    login,
    signup,
    logout,
    refreshSession,
    getAccessToken: async () => readStoredToken() || '',
    getUserId: () => session?.userId || null,
    getUserEmail: () => session?.email || null,
  }), [isAuthenticated, loading, login, signup, logout, refreshSession, session])
}
